import { Component, OnInit } from '@angular/core';
import { PfsService } from 'src/app/services/pfs.service';
import { ChartType } from 'angular-google-charts';

@Component({
  selector: 'app-pfs-allocation',
  templateUrl: './pfs-allocation.component.html'
})
export class PfsAllocationComponent implements OnInit {

  chartType!: ChartType;
  chartColumns: any[] = [];
  chartData: any[] = [];
  chartOptions: any = {};
  chartReady = false;

  constructor(private pfsService: PfsService) { }

  ngOnInit(): void {
    const clientId = 99; //Hardcoded for now
    const token = '';
    this.loadChart(clientId, token);
  }

  loadChart(clientId: number, token?: string): void {
    this.pfsService.getChartConfigByClient(clientId, token).subscribe(config => {
      if (!config?.chartType || !config.parameterColumns) {
        console.error('Chart config missing critical values.');
        return;
      }

      const rawType = (config.chartType || '').toLowerCase();
      const chartTypeMap: { [key: string]: ChartType } = {
        piechart: ChartType.PieChart,
        columnchart: ChartType.ColumnChart,
        barchart: ChartType.BarChart,
        linechart: ChartType.LineChart,
        areachart: ChartType.AreaChart,
        combochart: ChartType.ComboChart,
        table: ChartType.Table
      };

      const normalizedType: ChartType = chartTypeMap[rawType] || config.chartType;
      this.pfsService.getChartDataByClient(clientId, token).subscribe(data => {
        const chartDefinition = this.buildChartDefinition(normalizedType, config, data);
        if (chartDefinition) {
          this.chartType = chartDefinition.chartType;
          this.chartColumns = chartDefinition.chartColumns;
          this.chartOptions = chartDefinition.chartOptions;
          this.chartData = chartDefinition.chartData;
          this.chartReady = true;
        }
      });
    });
  }

  buildChartDefinition(
    chartType: ChartType,
    config: any,
    rawData: any[]
  ): {
    chartType: ChartType;
    chartColumns: any[];
    chartOptions: any;
    chartData: any[];
  } | null {
    let chartColumns: any[] = [];
    let chartData: any[] = [];
    let chartOptions: any = {};
    const paramCols = JSON.parse(config.parameterColumns || '{}');
    const xKey = paramCols.x;
    const yKey = paramCols.y;

    switch (chartType) {
      case 'PieChart': {
        chartColumns = [
          ['string', this.titleCase(xKey)],
          ['number', this.titleCase(yKey)]
        ];
        chartData = this.groupAndSum(rawData, xKey, yKey);
        chartOptions = {
          title: config.title || 'Pie Chart',
          is3D: true,
          pieSliceText: config.showTotal ? 'value-and-percentage' : 'percentage',
          legend: { position: 'right' },
          ...config.options
        };
        break;
      }

      case 'ColumnChart':
      case 'BarChart': {
        chartColumns = [
          ['string', this.titleCase(xKey)],
          ['number', this.titleCase(yKey)]
        ];
        chartData = rawData.map(row => [row[xKey], row[yKey]]);
        chartOptions = {
          title: config.title || `${chartType}`,
          hAxis: { title: this.titleCase(xKey) },
          vAxis: { title: this.titleCase(yKey) },
          legend: { position: 'top' },
          ...config.options
        };
        break;
      }

      case 'LineChart': {
        chartColumns = [
          ['string', this.titleCase(xKey)],
          ['number', this.titleCase(yKey)]
        ];
        chartData = rawData.map(row => [row[xKey], row[yKey]]);
        chartOptions = {
          title: config.title || 'Line Chart',
          curveType: 'function',
          legend: { position: 'bottom' },
          ...config.options
        };
        break;
      }

      default:
        console.warn(`Unsupported chart type: ${chartType}`);
        return null;
    }

    return {
      chartType,
      chartColumns,
      chartOptions,
      chartData
    };
  }

  groupAndSum(data: any[], xKey: string, yKey: string): [string, number][] {
  const grouped: { [key: string]: number } = {};
  data.forEach(row => {
    const x = row[xKey];
    const y = Number(row[yKey]) || 0;
    grouped[x] = (grouped[x] || 0) + y;
  });
  return Object.entries(grouped).map(([key, val]) => [key, val]);
}

  titleCase(str: string): string {
    return str
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, txt => txt[0].toUpperCase() + txt.substring(1).toLowerCase());
  }
}
